/**
 * WebSocket Types and Interfaces
 *
 * Provides type definitions for the WebSocket connection state machine,
 * error handling, and offline message queuing.
 */

/**
 * Connection state machine states
 */
export enum WebSocketConnectionState {
  /** Not connected, no active attempts */
  DISCONNECTED = 'DISCONNECTED',
  /** Initial connection attempt in progress */
  CONNECTING = 'CONNECTING',
  /** WebSocket open, sending authentication token */
  AUTHENTICATING = 'AUTHENTICATING',
  /** Fully authenticated and ready for messages */
  CONNECTED = 'CONNECTED',
  /** Lost connection, attempting to restore */
  RECONNECTING = 'RECONNECTING',
  /** Resuming a previous session after reconnect */
  RESUMING = 'RESUMING',
  /** Max retries exceeded, giving up */
  FAILED = 'FAILED',
  /** Intentionally paused (offline or background) */
  SUSPENDED = 'SUSPENDED',
}


/**
 * Error type categorization for smart error handling
 */
export enum WebSocketErrorType {
  /** DNS, timeout, connection refused - recoverable */
  NETWORK = 'NETWORK',
  /** Token invalid, expired - needs refresh */
  AUTHENTICATION = 'AUTH',
  /** 5xx errors, server overloaded - wait and retry */
  SERVER = 'SERVER',
  /** Invalid message format - not recoverable */
  PROTOCOL = 'PROTOCOL',
  /** Unknown error type */
  UNKNOWN = 'UNKNOWN',
}

/**
 * Suggested action based on error type
 */
export type WebSocketErrorAction =
  | 'retry'
  | 'refresh_token'
  | 'wait'
  | 'give_up';

/**
 * Structured error information for better handling
 */
export interface WebSocketError {
  /** Categorized error type */
  type: WebSocketErrorType;
  /** Human-readable error message */
  message: string;
  /** Original error or event that caused this */
  originalError?: Error | Event;
  /** When the error occurred */
  timestamp: Date;
  /** Whether this error can potentially be recovered from */
  isRecoverable: boolean;
  /** Recommended action to take */
  suggestedAction: WebSocketErrorAction;
}

/**
 * Priority levels for offline message queue
 */
export type MessagePriority = 'high' | 'normal' | 'low';

/**
 * Queued message for offline support
 */
export interface QueuedMessage {
  /** Unique identifier for the message */
  id: string;
  /** Message type (e.g., 'subscription', 'calculation') */
  type: string;
  /** Message payload data */
  data: Record<string, unknown>;
  /** Priority level for queue ordering */
  priority: MessagePriority;
  /** When the message was queued */
  queuedAt: Date;
}

/**
 * Connection metrics for monitoring and debugging
 */
export interface ConnectionMetrics {
  /** Current connection state */
  state: WebSocketConnectionState;
  /** Whether the device is online */
  isOnline: boolean;
  /** Current retry attempt number */
  retryAttempt: number;
  /** Maximum retry attempts allowed */
  maxRetries: number;
  /** Last error that occurred */
  lastError: WebSocketError | null;
  /** When connection was last established */
  lastConnectedAt: Date | null;
  /** When connection was last lost */
  lastDisconnectedAt: Date | null;
  /** Number of pending offline messages */
  pendingMessageCount: number;
}

/**
 * WebSocket close codes for reference
 * https://developer.mozilla.org/en-US/docs/Web/API/CloseEvent/code
 */
export const WS_CLOSE_CODES = {
  /** Normal closure */
  NORMAL: 1000,
  /** Going away (page navigation) */
  GOING_AWAY: 1001,
  /** Protocol error */
  PROTOCOL_ERROR: 1002,
  /** Unsupported data */
  UNSUPPORTED: 1003,
  /** No status received (abnormal) */
  NO_STATUS: 1005,
  /** Abnormal closure (no close frame) */
  ABNORMAL: 1006,
  /** Invalid frame payload */
  INVALID_PAYLOAD: 1007,
  /** Policy violation */
  POLICY_VIOLATION: 1008,
  /** Message too big */
  TOO_LARGE: 1009,
  /** Missing extension */
  MISSING_EXTENSION: 1010,
  /** Internal server error */
  SERVER_ERROR: 1011,
  /** Service restart - try again later */
  SERVICE_RESTART: 1012,
  /** Try again later */
  TRY_AGAIN_LATER: 1013,
  /** Bad gateway */
  BAD_GATEWAY: 1014,
  /** TLS handshake failure */
  TLS_FAILURE: 1015,
} as const;

/**
 * Event handler for WebSocket promise-based operations
 * Includes timeout support to prevent memory leaks from unresolved promises
 */
export interface WebSocketEventHandler {
  /** Promise resolve function */
  resolve: (value: unknown) => void;
  /** Promise reject function */
  reject: (reason?: unknown) => void;
  /** Timeout ID for automatic cleanup - cleared when handler resolves/rejects */
  timeoutId?: ReturnType<typeof setTimeout>;
  /** When the handler was created */
  createdAt: Date;
}

/**
 * DocNet gateway event types for org-wide notifications
 */
export enum DocNetGatewayEventType {
  LIST_CHANGED = 'list_changed',
  ISSUE_UPDATED = 'issue_updated',
  /**
   * Per-mission targeted update.  Mirrors ``ISSUE_UPDATED``; payload
   * carries ``mission_id`` plus a human-readable ``summary``.  Used
   * by the mission list page to refetch a single mission instead of
   * the whole paginated list on every status flip.  Coarse-grained
   * ``LIST_CHANGED("missions")`` is still emitted from CRUD
   * endpoints as a fallback for bulk changes.
   */
  MISSION_UPDATED = 'mission_updated',
  COMMENT_ADDED = 'comment_added',
  INBOX_ITEM_CREATED = 'inbox_item_created',
  DELEGATION_STARTED = 'delegation_started',
  CHILD_COMPLETED = 'child_completed',
  ESCALATION_CREATED = 'escalation_created',
  BUDGET_WARNING = 'budget_warning',
  MISSION_PROGRESS = 'mission_progress',
  MISSION_COMPLETED = 'mission_completed',
  /**
   * A mission's derived aggregate list changed — payload carries
   * ``{mission_id, kind}`` where ``kind`` is ``"email"`` or
   * ``"document"``.  Mission-detail tabs refetch the corresponding
   * endpoint on receipt.
   */
  MISSION_AGGREGATE_CHANGED = 'mission_aggregate_changed',
  /**
   * A new event was appended to a mission's timeline (status change,
   * tool call, approval, ...).  Coarse: payload carries only
   * ``{mission_id}`` so the panel can refetch the first page.  Same
   * pattern as MISSION_AGGREGATE_CHANGED — the canonical store is the
   * server, the WS event is a poke to refresh.
   */
  MISSION_TIMELINE_APPENDED = 'mission_timeline_appended',
  AGENT_START = 'agent_start',
  AGENT_CHUNK = 'agent_chunk',
  AGENT_COMPLETE = 'agent_complete',
  AGENT_TOOL_CALL = 'agent_tool_call',
  /** Run events delivered via issue:{id} channel */
  RUN_EVENT = 'run_event',
  RUN_STATUS = 'run_status',
  NEW_RUN = 'new_run',
}

/**
 * Server → client message types for WebSocket protocol (non-event messages)
 */
export enum DocNetWsMessageType {
  CONNECTED = 'connected',
  AUTH_FAILED = 'auth_failed',
  /** Client → server: subscribe to a channel */
  SUBSCRIBE = 'subscribe',
  /** Client → server: unsubscribe from a channel */
  UNSUBSCRIBE = 'unsubscribe',
  /** Server → client: subscription confirmed */
  SUBSCRIBED = 'subscribed',
  /** Server → client: unsubscription confirmed */
  UNSUBSCRIBED = 'unsubscribed',
  /** Server → client: invalid message or unknown type */
  ERROR = 'error',
  PING = 'ping',
  PONG = 'pong',
}

/**
 * Mission progress event data from the orchestrator
 */
export interface MissionProgressEvent {
  mission_id?: string;
  agent_name?: string;
  status?: string;
  response?: string;
  error?: string;
  agents_used?: string[];
  inbox_item_id?: string;
  // Full conversation array (sent with agent_complete events)
  conversation?: { role: string; text: string; timestamp: string; action?: string }[];
  last_modified_on?: string;
  // Approval fields
  waiting_reason?: string;
  waiting_agent?: string;
  // delegation events
  parent_issue_id?: string;
  child_issue_id?: string;
  parent_agent?: string;
  child_agent?: string;
  depth?: number;
  // tool call events
  tool_name?: string;
  tool_call_id?: string;
  arguments?: Record<string, unknown>;
  // mission aggregate-changed events — ``kind`` tells the receiver
  // which aggregate list (``"email"`` / ``"document"``) to refetch.
  kind?: string;
}

/**
 * Default timeout for WebSocket event handlers (30 seconds)
 * Handlers that don't receive a response within this time will be rejected
 */
export const WS_EVENT_HANDLER_TIMEOUT_MS = 30000;

/**
 * Check if a close code indicates a recoverable situation
 */
export function isRecoverableCloseCode(code: number): boolean {
  // Normal or intentional closes are not "recoverable" (shouldn't retry)
  if (code === WS_CLOSE_CODES.NORMAL || code === WS_CLOSE_CODES.GOING_AWAY) {
    return false;
  }
  // These indicate server issues that may resolve
  if (
    code === WS_CLOSE_CODES.SERVICE_RESTART ||
    code === WS_CLOSE_CODES.TRY_AGAIN_LATER ||
    code === WS_CLOSE_CODES.SERVER_ERROR
  ) {
    return true;
  }
  // Abnormal closure - likely network issue, should retry
  if (code === WS_CLOSE_CODES.ABNORMAL || code === WS_CLOSE_CODES.NO_STATUS) {
    return true;
  }
  // Default to recoverable for unknown codes
  return true;
}
